import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { GET_NAMED_FILTERS } from '../../Formulas/apollo';
import {
  deleteDictionaryItemAction,
  setDictionaryListAction,
} from '../../../redux/slices/dictionary';
import { DictionaryTypeEnum } from '../../../utils/interfaces';
import {
  IGetNamedFiltersQuery,
  IGetNamedFiltersQueryVariables,
  INamedFilterFieldsFragment,
  IUpdateNamedFilterMutation,
  IUpdateNamedFilterMutationVariables,
  ManagedEntityType,
  PermissionKey,
} from '../../../utils/apollo/graphqlSchema';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import DictionaryHeader from '../utils/DictionaryHeader';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import usePermissionSchemas from '../../PermissionControls/hooks/usePermissionSchemas';
import { selectOpenedModalType } from '../../../redux/selectors/modal';
import { ModalSliceType, openModal } from '../../../redux/slices/modal';
import DeleteNamedFilterModal from '../utils/DeleteNamedFilterModal';
import { UPDATE_NAMED_FILTER } from '../../../utils/apollo/virtualEvents';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import {
  NAMED_FILTER_ADD_CATEGORY,
  NAMED_FILTER_REMOVE_CATEGORY,
} from '../../../utils/apollo/filters/query';
import { useDebounce } from '../../../utils/hooks';
import toastMessage from '../../../utils/toastMessage';
import { ContentLoader } from '../../../components/Loaders';
import DictionaryNoResults from '../DictionaryNoResults';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { pageViewed } from '../../../utils/analytics/events/page/events';
import FiltersTable from './FiltersTable';
import useApplyTags from '../useApplyTags';
import { CategoryEntityType } from '../../../components/Categories/utils';
import { RowSelectionState } from '@tanstack/react-table';
import { useGlobalApplyTags } from '../utils/useGlobalApplyTags';
const FilterDictionary = () => {
  const { track } = useAnalytics();
  useEffect(() => {
    track(
      pageViewed({
        category: 'dictionary',
        name: 'filter',
        id: null,
      }),
    );
  }, [track]);

  const dispatch = useAppDispatch();

  const { selectedSchemaId, permittedSchemaIds } = usePermissionSchemas({
    requestedPermissionKey: PermissionKey.FilterRead,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const { checkIsPermitted } = usePermission();
  const isPermittedToDeleteNamedFilter = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.FilterDelete],
    schemaId: selectedSchemaId,
  });
  const isPermittedToEditNamedFilter = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.FilterEdit],
    schemaId: selectedSchemaId,
  });
  const isPermittedToAssignCategory = checkIsPermitted({
    requiredPermissionKeys: [
      PermissionKey.CategoryCreate,
      PermissionKey.FilterEdit,
    ],
    schemaId: selectedSchemaId,
  });

  const isDeleteModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.DeleteNamedFilter, ''),
  );
  const filters = useAppSelector(
    (state) => state.dictionary.filter,
  ) as INamedFilterFieldsFragment[];
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const onFetchDictionaryFilters = (res: IGetNamedFiltersQuery) => {
    const entities = res?.namedFilters?.entities;
    if (entities && entities.length >= 0) {
      dispatch(
        setDictionaryListAction({
          newValues: entities,
          dictionaryType: DictionaryTypeEnum.DictionaryFilter,
        }),
      );
    }
  };

  const { loading: filtersLoading, refetch: refetchFilters } = useQuery<
    IGetNamedFiltersQuery,
    IGetNamedFiltersQueryVariables
  >(GET_NAMED_FILTERS, {
    variables: {
      includeIds: [],
      schemaId: selectedSchemaId,
      term: debouncedSearchQuery,
      pagination: {
        first: 0,
        after: 0,
      },
    },
    onCompleted: onFetchDictionaryFilters,
  });
  const handleDeleteNamedFilter = (itemId: string) => {
    dispatch(
      deleteDictionaryItemAction({
        itemId,
        dictionaryType: DictionaryTypeEnum.DictionaryFilter,
      }),
    );
  };

  const handleOpenDeleteNamedFilter = (filter: INamedFilterFieldsFragment) => {
    if (!isPermittedToDeleteNamedFilter) {
      return;
    }
    dispatch(
      openModal({ type: ModalSliceType.DeleteNamedFilter, data: filter }),
    );
  };

  const handleEditMutationComplete = ({
    namedFilterEdit,
  }: IUpdateNamedFilterMutation) => {
    const { entity, errors } = namedFilterEdit;

    if (printGraphqlErrors(errors)) {
      return;
    }

    if (entity) {
      toastMessage({
        message: `${entity.name} has been updated.`,
        type: 'info',
      });
    }
  };

  const [editNamedFilter] = useMutation<
    IUpdateNamedFilterMutation,
    IUpdateNamedFilterMutationVariables
  >(UPDATE_NAMED_FILTER, {
    onCompleted: handleEditMutationComplete,
  });

  const onNameChange = async (
    namedFilter: INamedFilterFieldsFragment,
    newValue: string,
  ) => {
    const { data } = await editNamedFilter({
      variables: {
        input: {
          name: newValue.trim(),
          conditions: null,
          logicOps: null,
          comment: null,
          description: null,
          namedFilterId: namedFilter.id,
          categories: null,
        },
      },
    });

    if (data) {
      await refetchFilters();
    }
  };

  const onDescriptionChange = async (
    namedFilter: INamedFilterFieldsFragment,
    newValue: string,
  ) => {
    const { data } = await editNamedFilter({
      variables: {
        input: {
          name: null,
          conditions: null,
          logicOps: null,
          comment: null,
          description: newValue.trim(),
          namedFilterId: namedFilter.id,
          categories: null,
        },
      },
    });

    if (data) {
      await refetchFilters();
    }
  };

  const handleSubmitNewTags = () => {
    refetchFilters();
    setRowSelection({});
  };

  const handleApplyTags = useApplyTags({
    entityType: CategoryEntityType.NamedFilter,
    addCategoryMutation: NAMED_FILTER_ADD_CATEGORY,
    removeCategoryMutation: NAMED_FILTER_REMOVE_CATEGORY,
    managedEntityType: ManagedEntityType.Filter,
    getEntityId: (entity: INamedFilterFieldsFragment) => entity.id,
    getEntityCategories: (entity: INamedFilterFieldsFragment) =>
      entity.categories,
    onSuccess: handleSubmitNewTags,
  });

  const handleGlobalApplyTags = useGlobalApplyTags({
    rowSelection,
    entities: filters,
    selectedSchemaId,
    handleApplyTags,
    getEntityId: (entity) => entity.id,
  });

  const selectedEntityCategories = filters
    .filter((filter) => rowSelection[filter.id])
    .map((filter) => ({ categories: filter.categories }));

  return (
    <>
      <DictionaryHeader
        selectedSchemaId={selectedSchemaId}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        title={'Filter'}
        dictionaryItemCount={filters.length}
        permittedSchemaIds={permittedSchemaIds}
        onApplyTags={handleGlobalApplyTags}
        isPermittedToAssignCategory={isPermittedToAssignCategory}
        showApplyTags={Object.keys(rowSelection).length > 0}
        selectedEntities={selectedEntityCategories}
      />
      {filtersLoading ? (
        <ContentLoader />
      ) : filters.length === 0 ? (
        <DictionaryNoResults entityType={DictionaryTypeEnum.DictionaryFilter} />
      ) : (
        <FiltersTable
          filters={filters}
          schemaId={selectedSchemaId}
          onApplyTags={handleApplyTags}
          onNameChange={onNameChange}
          onDescriptionChange={onDescriptionChange}
          onDeleteFilter={handleOpenDeleteNamedFilter}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          isPermittedToEditNamedFilter={isPermittedToEditNamedFilter}
          isPermittedToDeleteNamedFilter={isPermittedToDeleteNamedFilter}
          isPermittedToAssignCategory={isPermittedToAssignCategory}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteNamedFilterModal onSubmit={handleDeleteNamedFilter} />
      )}
    </>
  );
};

export default FilterDictionary;
